import React, { useRef, useEffect, useState, useCallback } from "react";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import CssBaseline from "@mui/material/CssBaseline";
import AppBar from "@mui/material/AppBar";
import MenuIcon from "@mui/icons-material/Menu";
import { alpha } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import axios from "axios";
import LinearProgress from "@mui/material/LinearProgress";
import CircleIcon from "@mui/icons-material/Circle";
const drawerWidth = 300;

const useStyles = makeStyles((theme) => ({
  appBar: {
    [theme.breakpoints.up("sm")]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
  },
  appBarSpacer: theme.mixins.toolbar,
  appBarReviewOrder: {
    position: "fixed",
  },
  appBarBottomReviewOrder: {
    top: "auto",
    bottom: 0,
  },
  titleBarReviewOrder: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  appBarTop: {
    [theme.breakpoints.up("sm")]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
  },
  appBarBottom: {
    top: "auto",
    bottom: 0,
    [theme.breakpoints.up("sm")]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      display: "none",
    },
  },
  search: {
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      marginLeft: theme.spacing(3),
      width: "auto",
    },
    roota: {
      ...theme.typography.button,
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(1),
    },
  },
}));
const filter = createFilterOptions();

export default function Accounts(props) {
  const classes = useStyles();
  const [searchAccountTerm, setSearchAccountTerm] = useState({ name: "" });
  const elementRef = useRef(null);
  const [, setAppBarWidth] = useState(0);
  const [loadAccounts, setLoadAccounts] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [autoSuggestAccounts, setAutoSuggestAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [autoCompleteSearchOptionMessage, setAutoCompleteSearchOptionMessage] =
    useState("Searching...");
  const [hasMoreAccountToLoad, setHasMoreAccountToLoad] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const lastAccountElementObserver = useRef();
  const lastAccountElement = useCallback(
    (node) => {
      if (isLoading) {
        return;
      }
      if (lastAccountElementObserver.current) {
        lastAccountElementObserver.current.disconnect();
      }
      lastAccountElementObserver.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMoreAccountToLoad) {
          lastAccountElementObserver.current.disconnect();
          setPageNumber((prevPageNumber) => prevPageNumber + 1);
          setLoadAccounts(true);
        }
      });

      if (node) {
        lastAccountElementObserver.current.observe(node);
      }
    },
    [isLoading, hasMoreAccountToLoad]
  );

  const [accountsLabel, setAccountLabel] = useState({});
  const [accountClassificationLabel, setAccountClassificationLabel] = useState({});
  const [isLoadingLabel, setIsLoadingLabel] = useState(true);

  useEffect(() => {
    if (props.reloadAccounts) {
      setAccounts([]);
      setPageNumber(1);
      setLoadAccounts(true);
      props.setReloadAccounts(false);
    }
  }, [props.reloadAccounts]);

  useEffect(() => {
    if (!isLoadingLabel) {
      return;
    }

    const getAccountsLabel = () => {
      const options = {
        method: "POST",
        url: "/TransactiLabels/getTransactiLiteLabels.json",
      };

      axios(options)
        .then((res) => {
          const labels = res.data.response;
          const accountLabel = labels.accounts;
          const accountClassificationsLabel = labels.account_classifications;

          setAccountLabel(accountLabel);
          setAccountClassificationLabel(accountClassificationsLabel);
          setIsLoadingLabel(false);
        })
        .catch((error) => {
          console.log(error);
        });
    };

    getAccountsLabel();
  }, [accountsLabel, isLoadingLabel]);

  function autoComplete() {
    return (
      <Autocomplete
        value={searchAccountTerm.name}
        noOptionsText={autoCompleteSearchOptionMessage}
        onChange={(event, newValue) => {
          if (newValue == null || newValue.name == "") {
            searchAccountTerm.name = "";
            return;
          }
          searchAccountTerm.name = newValue.name;
          props.setReloadAccounts(true);
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={autoSuggestAccounts}
        isOptionEqualToValue={() => true}
        getOptionLabel={(option) => {
          // Value selected with enter, right from the input
          if (typeof option === "string") {
            return option;
          }
          // Add "xxx" option created dynamically
          if (option.inputValue) {
            return option.inputValue;
          }
          // Regular option
          return option.name;
        }}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.name + option.id}>
              {option.name}
            </li>
          );
        }}
        style={{ width: "100%", paddingLeft: "8px", outline: "none" }}
        renderInput={(params) => (
          <TextField
            style={{ background: "white" }}
            variant="standard"
            {...params}
            InputProps={{ ...params.InputProps, disableUnderline: true }}
            onChange={(event) => {
              setSearchAccountTerm({ name: event.target.value });
            }}
          />
        )}
      />
    );
  }
  function search() {
    return (
      <div
        position="static"
        style={{ border: ".1rem solid #989898", borderRadius: "4px", margin: "2px" }}
      >
        <Toolbar variant="dense" style={{ padding: "0" }}>
          <Box flexGrow={1}>{autoComplete()}</Box>
          <Box>
            <IconButton
              className={classes.margin}
              size="large"
              onClick={() => props.setReloadAccounts(true)}
            >
              <SearchIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </div>
    );
  }

  function accountElement(account) {
    return (
      <div style={{ marginLeft: ".5em", width: "100%" }}>
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{ display: "flex", alignItems: "center", gap: "0.3em" }}
        >
          <CircleIcon sx={{ fontSize: "15px", color: account.hasSalesToday ? "green" : "red" }} />
          {account.account_name}
        </Typography>
        <Typography variant="body1">
          {" "}
          {account.owner_first_name} {account.owner_middle_name} {account.owner_last_name}
        </Typography>
        <Typography variant="body1">#: {account.contact_number}</Typography>
        <Typography variant="body1">
          {account.street} {account.city_municipality} {account.province}
        </Typography>
      </div>
    );
  }

  const itemOnClick = (account) => {
    props.openDialogInputHandleClick(account);
  };
  useEffect(() => {
    if (!loadAccounts) {
      return;
    }
    setAppBarWidth(elementRef.current.getBoundingClientRect().width);
    fetchAccounts(searchAccountTerm.name);
  }, [loadAccounts, searchAccountTerm.name]);

  useEffect(() => {
    fetchAccountForAutoSuggest(searchAccountTerm.name);
  }, [searchAccountTerm]);

  function fetchAccounts(searchTerm) {
    setIsLoading(true);

    const options = {
      method: "POST",
      url: "/SellerAccounts/getSellerAccounts.json",
      data: objectToFormData({ search_term: searchTerm, page_number: pageNumber }),
    };
    axios(options)
      .then((res) => {
        setIsLoading(false);
        setLoadAccounts(false);

        const isSuccess = res.data.response.is_success;
        if (!isSuccess) {
          alert("Error encounter");
          return;
        }

        const accountsFromServer = res.data.response.seller_accounts;
        setHasMoreAccountToLoad(accountsFromServer.length > 0);
        setAccounts(accounts.concat(accountsFromServer));
      })
      .catch((error) => {
        setIsLoading(false);
        alert("Error encounter");
        console.log(error);
      });
  }

  function fetchAccountForAutoSuggest(searchTerm) {
    setAutoCompleteSearchOptionMessage("Searching...");
    const options = {
      method: "POST",
      url: "/SellerAccounts/getSellerAccountsForAutoSuggest.json",
      data: objectToFormData({ search_term: searchTerm }),
    };
    axios(options)
      .then((res) => {
        const isSuccess = res.data.response.is_success;

        if (!isSuccess) {
          alert("Error encounter");
          return;
        }
        const suggestedAccounts = res.data.response.seller_accounts;
        if (suggestedAccounts.length <= 0) {
          setAutoCompleteSearchOptionMessage("No record found");
        }

        setAutoSuggestAccounts(suggestedAccounts);
      })
      .catch((error) => {
        alert("Error encounter");
        console.log(error);
      });
  }

  const objectToFormData = (object) =>
    Object.keys(object).reduce((formData, key) => {
      formData.append(key, object[key]);

      return formData;
    }, new FormData());

  return (
    <div>
      <CssBaseline />
      <AppBar position="fixed" className={classes.appBarTop} aria-haspopup="true" ref={elementRef}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={props.handleDrawerToggle}
            className={classes.menuButton}
            size="large"
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap>
            Transacti {accountsLabel.plural}
          </Typography>
        </Toolbar>
        <Box sx={{ backgroundColor: "white" }}>
          {search()}
          {loadAccounts ? (
            <LinearProgress
              color="secondary"
              style={{ marginLeft: ".1rem", marginRight: ".1rem" }}
            />
          ) : (
            ""
          )}
        </Box>
      </AppBar>
      <Box sx={{ marginTop: "60px" }}>
        <List>
          {accounts.map((account, index) => {
            return accounts.length === index + 1 ? (
              <div
                key={index + account.id + account.account_name}
                onClick={(event) => {
                  itemOnClick(account);
                }}
              >
                <ListItem
                  ref={lastAccountElement}
                  key={index + account.id + account.account_name}
                  button
                  divider
                >
                  {/* <ListItemAvatar>
                    <Avatar
                      sx={{ width: 56, height: 56 }}
                      src={account.image_path === "" ? account.image : account.image_path}
                    />
                  </ListItemAvatar> */}
                  {accountElement(account)}
                </ListItem>
              </div>
            ) : (
              <div
                key={index + account.id + account.account_name}
                onClick={(event) => {
                  itemOnClick(account);
                }}
              >
                <ListItem button divider>
                  {/* <ListItemAvatar>
                    <Avatar
                      sx={{ width: 56, height: 56 }}
                      src={account.image_path === "" ? account.image : account.image_path}
                    />
                  </ListItemAvatar> */}
                  {accountElement(account)}
                </ListItem>
              </div>
            );
          })}
        </List>
      </Box>
    </div>
  );
}
